import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import * as XLSX from "xlsx";
import AdminService from "../../../api/services/adminService";
import { clearAuth } from "../../../utils/authStorage";
import {
  FIELD_HELP,
  LINE_STATUS_BADGE,
  PLAN_STATUS_KIND,
  STATEMENT_PAGE_LIMIT,
  TYPE_BADGE,
  asNum,
  copyText,
  formatDateShort,
  formatDateTime,
  formatMoney,
  hasPositive,
  isMongoId,
  isUnauthorized,
  payoutTypeLabel,
  subscriptionShortId,
  userDisplayName,
} from "./stakingStatementUtils";
import "../Home/Homepage.css";
import "../Trading/AllOpenOrdersPage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "./StakingStakesPage.css";
import "./StakingStatementPage.css";

const TABS = [
  { id: "summary", label: "Summary" },
  { id: "plans", label: "Plans" },
  { id: "statement", label: "Statement" },
];

const EMPTY_PAGINATION = {
  page: 1,
  limit: STATEMENT_PAGE_LIMIT,
  total: 0,
  total_pages: 1,
  has_next: false,
  has_prev: false,
};

/** Backend caps statement lines at 100 per request. */
const EXPORT_STATEMENT_LIMIT = 100;
const EXPORT_MAX_PAGES = 500;

const exportAmount = (value) => {
  if (value == null || value === "") return "";
  const n = asNum(value);
  return Number.isFinite(n) ? n : "";
};

const STATEMENT_HEADERS = [
  "Date",
  "Type",
  "Payout Type",
  "Description",
  "Plan",
  "Subscription ID",
  "Currency",
  "Debit",
  "Credit",
  "Interest",
  "Principal",
  "Status",
  "Running Credited",
  "Running Remaining",
];

const planExportLabel = (plan) => {
  const pkg = plan?.package || {};
  const currency = plan?.currency || pkg.currency || "";
  return (
    [currency, pkg.duration_days != null ? `${pkg.duration_days}d` : null, pkg.return_percentage != null ? `${pkg.return_percentage}%` : null]
      .filter(Boolean)
      .join(" · ") || "Plan"
  );
};

const Help = ({ field }) => {
  const text = FIELD_HELP[field];
  if (!text) return null;
  return (
    <i className="fas fa-info-circle ss-help" title={text} aria-label={text} />
  );
};

const AmountCard = ({ title, field, value, currency, hint, tone, hideIfZero }) => {
  if (hideIfZero && !hasPositive(value)) return null;
  return (
    <article className={`ss-card ss-card--${tone || "default"}`}>
      <header className="ss-card__head">
        <span>{title}</span>
        <Help field={field} />
      </header>
      <strong>{formatMoney(value, currency)}</strong>
      {hint ? <small>{hint}</small> : null}
    </article>
  );
};

const ProgressBar = ({ label, paid, total }) => {
  const denom = asNum(total);
  if (denom <= 0) return null;
  const pct = Math.min(100, Math.max(0, (asNum(paid) / denom) * 100));
  return (
    <div className="ss-progress">
      <div className="ss-progress__meta">
        <span>{label}</span>
        <span>{pct.toFixed(1)}%</span>
      </div>
      <div className="ss-progress__track" aria-hidden>
        <div className="ss-progress__fill" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const TypeBadge = ({ type }) => {
  const meta = TYPE_BADGE[String(type || "").toUpperCase()] || {
    label: type || "—",
    kind: "grey",
  };
  return <span className={`ss-badge ss-badge--${meta.kind}`}>{meta.label}</span>;
};

const LineStatusBadge = ({ status }) => {
  const key = String(status || "").toLowerCase();
  const meta = LINE_STATUS_BADGE[key] || { label: status || "—", kind: "grey" };
  return <span className={`ss-badge ss-badge--${meta.kind}`}>{meta.label}</span>;
};

const PlanStatusBadge = ({ status }) => {
  const key = String(status || "").toUpperCase();
  const kind = PLAN_STATUS_KIND[key] || "grey";
  return <span className={`ss-badge ss-badge--${kind}`}>{status || "—"}</span>;
};

const StakingStatementDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("summary");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canRetry, setCanRetry] = useState(false);

  const [selectedCurrencyId, setSelectedCurrencyId] = useState("");
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [subscriptionId, setSubscriptionId] = useState("");
  const [includePending, setIncludePending] = useState(true);
  const [statementPage, setStatementPage] = useState(1);
  const [allSubscriptions, setAllSubscriptions] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);

  const goBackToList = useCallback(() => {
    navigate("/dashboard/staking/statement");
  }, [navigate]);

  const buildParams = useCallback(() => {
    const params = {
      include_pending: includePending ? "true" : "false",
      statement_page: statementPage,
      statement_limit: STATEMENT_PAGE_LIMIT,
    };
    if (isMongoId(selectedCurrencyId)) params.currency_id = selectedCurrencyId;
    if (isMongoId(subscriptionId)) params.subscription_id = subscriptionId;
    return params;
  }, [includePending, statementPage, selectedCurrencyId, subscriptionId]);

  const fetchDetail = useCallback(async () => {
    if (!userId) {
      setError("Invalid user ID format");
      setCanRetry(false);
      toast.error("Invalid user ID format");
      goBackToList();
      return;
    }
    setLoading(true);
    setError("");
    setCanRetry(false);
    try {
      const res = await AdminService.getUserStakingStatement(userId, buildParams());
      if (isUnauthorized(res)) {
        clearAuth();
        window.location.href = "/";
        return;
      }
      const status = Number(res?.httpStatus);
      if (status === 400 || status === 404) {
        toast.error(res?.message || (status === 404 ? "User not found" : "Invalid user ID format"));
        goBackToList();
        return;
      }
      if (res?.success === false) {
        setData(null);
        setError(res?.message || "Failed to fetch user staking statement");
        setCanRetry(status >= 500 || !status);
        return;
      }
      const payload = res?.data && !Array.isArray(res.data) ? res.data : null;
      if (!payload) {
        setData(null);
        setError(res?.message || "Failed to fetch user staking statement");
        setCanRetry(true);
        return;
      }
      setData(payload);
      const currencies = Array.isArray(payload.by_currency) ? payload.by_currency : [];
      setCurrencyOptions((prev) => {
        if (!selectedCurrencyId && currencies.length) return currencies;
        return prev.length >= currencies.length ? prev : currencies.length ? currencies : prev;
      });
      if (!subscriptionId && Array.isArray(payload.subscriptions)) {
        setAllSubscriptions(payload.subscriptions);
      }
    } catch (err) {
      setData(null);
      setError(err?.message || "Failed to fetch user staking statement");
      setCanRetry(true);
    } finally {
      setLoading(false);
    }
  }, [userId, buildParams, goBackToList, subscriptionId, selectedCurrencyId]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const user = data?.user || {};
  const counts = data?.plan_counts || {};
  const paused = String(user.payout_status || "").toLowerCase() === "inactive";
  const name = userDisplayName(user);

  const selectedCurrency = useMemo(() => {
    const list = Array.isArray(data?.by_currency) ? data.by_currency : [];
    if (!list.length) return null;
    return (
      list.find(
        (c) => c.currency_id === selectedCurrencyId || c.currency === selectedCurrencyId
      ) || list[0]
    );
  }, [data, selectedCurrencyId]);

  const currencyCode = selectedCurrency?.currency || "";
  const subscriptions = Array.isArray(data?.subscriptions) ? data.subscriptions : [];
  const statement = Array.isArray(data?.statement) ? data.statement : [];
  const statementPagination = data?.statement_pagination || EMPTY_PAGINATION;
  const noPlans = (counts.total || subscriptions.length) === 0;

  const handleCopyUuid = async () => {
    const ok = await copyText(user.uuid);
    if (ok) toast.success("UUID copied");
    else toast.error("Could not copy UUID");
  };

  const viewPlanInStatement = (id) => {
    setSubscriptionId(id || "");
    setTab("statement");
    setStatementPage(1);
  };

  const handleExportExcel = async () => {
    if (!userId) return;
    setExportLoading(true);
    try {
      let currentPage = 1;
      let lastPage = 1;
      let payload = null;
      const lines = [];

      do {
        const params = {
          include_pending: includePending ? "true" : "false",
          statement_page: currentPage,
          statement_limit: EXPORT_STATEMENT_LIMIT,
        };
        if (isMongoId(selectedCurrencyId)) params.currency_id = selectedCurrencyId;
        if (isMongoId(subscriptionId)) params.subscription_id = subscriptionId;

        const res = await AdminService.getUserStakingStatement(userId, params);
        if (isUnauthorized(res)) {
          clearAuth();
          window.location.href = "/";
          return;
        }
        if (res?.success === false) {
          throw new Error(res?.message || "Failed to fetch statement for export.");
        }
        const pageData = res?.data && !Array.isArray(res.data) ? res.data : null;
        if (!pageData) {
          throw new Error(res?.message || "Failed to fetch statement for export.");
        }
        if (!payload) payload = pageData;
        const pageLines = Array.isArray(pageData.statement) ? pageData.statement : [];
        lines.push(...pageLines);

        const pag = pageData.statement_pagination || {};
        lastPage = Math.max(1, Number(pag.total_pages) || 1);
        const hasNext =
          typeof pag.has_next === "boolean" ? pag.has_next : currentPage < lastPage;
        currentPage += 1;
        if (!hasNext) break;
      } while (currentPage <= lastPage && currentPage <= EXPORT_MAX_PAGES);

      const exportUser = payload?.user || user || {};
      const exportName = userDisplayName(exportUser);
      const currencies = Array.isArray(payload?.by_currency) ? payload.by_currency : [];
      const plans = Array.isArray(payload?.subscriptions) ? payload.subscriptions : [];
      const countsForExport = payload?.plan_counts || {};

      if (!currencies.length && !plans.length && !lines.length) {
        toast.error("Nothing to export for this statement.");
        return;
      }

      const summaryRows = (currencies.length ? currencies : [{}]).map((ccy) => ({
        Name: exportName === "—" ? "" : exportName,
        UUID: exportUser.uuid ?? "",
        Email: exportUser.email ?? "",
        Phone: exportUser.phone ?? "",
        "Payout Status": exportUser.payout_status ?? "",
        "Active Plans": countsForExport.active ?? "",
        "Completed Plans": countsForExport.completed ?? "",
        "Cancelled Plans": countsForExport.cancelled ?? "",
        Currency: ccy.currency ?? "",
        Staked: exportAmount(ccy.staked),
        "Expected Return": exportAmount(ccy.expected_return),
        "Interest Total": exportAmount(ccy.interest_total),
        "Payout Paid": exportAmount(ccy.payout_paid),
        "Payout Remaining": exportAmount(ccy.payout_remaining),
        "Interest Paid": exportAmount(ccy.interest_paid),
        "Interest Remaining": exportAmount(ccy.interest_remaining),
        "Principal Paid": exportAmount(ccy.principal_paid),
        "Principal Remaining": exportAmount(ccy.principal_remaining),
        Forfeited: exportAmount(ccy.payout_forfeited),
        Refunded: exportAmount(ccy.refunded),
        "Cancel Fee": exportAmount(ccy.cancel_fee),
      }));

      const planRows = plans.map((plan) => {
        const pkg = plan.package || {};
        const ccy = plan.currency || pkg.currency || "";
        return {
          "Subscription ID": plan.subscription_id || plan._id || plan.id || "",
          Plan: planExportLabel(plan),
          Currency: ccy,
          Status: plan.status ?? "",
          "Payout Status": plan.payout_status ?? "",
          Type: plan.subscription_type ?? "",
          "With Principal": plan.with_principal_amount ? "Yes" : "No",
          Staked: exportAmount(plan.staked),
          "Expected Return": exportAmount(plan.expected_return),
          "Payout Paid": exportAmount(plan.payout_paid),
          "Payout Remaining": exportAmount(plan.payout_remaining),
          "Interest Paid": exportAmount(plan.interest_paid),
          "Interest Total": exportAmount(plan.interest_total),
          "Periods Paid": plan.paid_days ?? "",
          "Total Periods": plan.total_periods ?? "",
          Start: formatDateShort(plan.start_date),
          End: formatDateShort(plan.end_date),
          "Next Payout": formatDateShort(plan.next_payout_date),
          "Last Payout": formatDateTime(plan.last_payout_at),
          "Cancel Fee": exportAmount(plan.cancel_fee),
          Refunded: exportAmount(plan.refunded),
          "Forfeited ROI": exportAmount(plan.payout_forfeited),
        };
      });

      const statementRows = lines.map((line) => ({
        Date: formatDateTime(line.date),
        Type: line.type ?? "",
        "Payout Type": payoutTypeLabel(line.payout_type),
        Description: line.description ?? "",
        Plan: line.period_label ?? "",
        "Subscription ID": line.subscription_id ?? "",
        Currency: line.currency ?? "",
        Debit: exportAmount(line.debit),
        Credit: exportAmount(line.credit),
        Interest: exportAmount(line.interest),
        Principal: exportAmount(line.principal),
        Status: line.status ?? "",
        "Running Credited": exportAmount(line.running_credited),
        "Running Remaining": exportAmount(line.running_remaining),
      }));

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, XLSX.utils.json_to_sheet(summaryRows), "Summary");
      XLSX.utils.book_append_sheet(
        workbook,
        planRows.length ? XLSX.utils.json_to_sheet(planRows) : XLSX.utils.aoa_to_sheet([["Subscription ID"]]),
        "Plans"
      );
      XLSX.utils.book_append_sheet(
        workbook,
        statementRows.length
          ? XLSX.utils.json_to_sheet(statementRows)
          : XLSX.utils.aoa_to_sheet([STATEMENT_HEADERS]),
        "Statement"
      );

      const slug = String(exportUser.uuid || userId).replace(/[^\w.-]+/g, "_");
      const dateStamp = new Date().toISOString().slice(0, 10);
      XLSX.writeFile(workbook, `staking_statement_${slug}_${dateStamp}.xlsx`);
      toast.success(`Exported ${lines.length} statement line(s) to Excel.`);
    } catch (err) {
      toast.error(err?.message || "Failed to export Excel file.");
    } finally {
      setExportLoading(false);
    }
  };

  const subscriptionOptions = allSubscriptions.length ? allSubscriptions : subscriptions;

  return (
    <main className="dashboard-main dashboard-subpage">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/staking/statement">Staking Statement</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>User</span>
          </p>
          <h1 className="dashboard-hero__title">User Staking Statement</h1>
          <p className="dashboard-hero__subtitle">
            Payout, remaining, and interest for this staker
          </p>
        </div>
        <div className="dashboard-hero__icon">
          <i className="fas fa-file-invoice" />
        </div>
      </section>

      <div className="dashboard-body">
        <div className="ss-detail-toolbar">
          <Link to="/dashboard/staking/statement" className="ss-back-btn">
            <i className="fas fa-arrow-left" /> Back to Staking Statement
          </Link>
          <button
            type="button"
            className="staking-stakes__export-btn"
            onClick={handleExportExcel}
            disabled={exportLoading || loading || !data}
            title="Download this user's summary, plans, and statement as Excel"
          >
            <i className={`fas ${exportLoading ? "fa-spinner fa-spin" : "fa-file-excel"}`} />
            {exportLoading ? "Exporting…" : "Download Excel"}
          </button>
        </div>

        {loading && !data ? (
          <div className="openorders-empty">
            <i className="fas fa-spinner fa-spin" /> Loading…
          </div>
        ) : error && !data ? (
          <div className="ss-empty">
            <i className="fas fa-exclamation-circle" />
            <p>{error}</p>
            {canRetry ? (
              <button type="button" className="cp-btn cp-btn--primary" onClick={fetchDetail}>
                Retry
              </button>
            ) : (
              <button type="button" className="cp-btn cp-btn--outline" onClick={goBackToList}>
                Back to list
              </button>
            )}
          </div>
        ) : data ? (
          <>
            <header className="ss-user-head">
              <div>
                <h2>
                  {name}
                  {paused ? <span className="ss-paused-badge">Payout paused</span> : null}
                </h2>
                <p>
                  {user.uuid ? (
                    <button type="button" className="ss-uuid-btn" onClick={handleCopyUuid}>
                      {user.uuid} <i className="fas fa-copy" />
                    </button>
                  ) : null}
                  {user.email ? <span>{user.email}</span> : null}
                  {user.phone ? <span>{user.phone}</span> : null}
                </p>
              </div>
              <p className="ss-user-head__plans">
                Plans: <strong>{counts.active ?? 0} Active</strong>
                {" · "}
                {counts.completed ?? 0} Completed
                {" · "}
                {counts.cancelled ?? 0} Cancelled
              </p>
            </header>

            {currencyOptions.length ? (
              <div className="ss-chips ss-chips--currency" role="tablist" aria-label="Currency">
                {currencyOptions.map((ccy, index) => {
                  const id = ccy.currency_id || ccy.currency;
                  const active =
                    selectedCurrencyId === id || (!selectedCurrencyId && index === 0);
                  return (
                    <button
                      key={id}
                      type="button"
                      className={`ss-chip ${active ? "ss-chip--active" : ""}`}
                      onClick={() => {
                        setSelectedCurrencyId(id);
                        setStatementPage(1);
                      }}
                    >
                      {ccy.currency}
                      {ccy.currency_fullname ? ` · ${ccy.currency_fullname}` : ""}
                    </button>
                  );
                })}
              </div>
            ) : null}

            <div className="ss-tabs" role="tablist">
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`ss-tab ${tab === item.id ? "ss-tab--active" : ""}`}
                  onClick={() => setTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {loading ? (
              <div className="openorders-empty">
                <i className="fas fa-spinner fa-spin" /> Updating…
              </div>
            ) : null}

            {error ? (
              <div className="ss-error-banner">
                <p>{error}</p>
                {canRetry ? (
                  <button type="button" className="cp-btn cp-btn--primary" onClick={fetchDetail}>
                    Retry
                  </button>
                ) : null}
              </div>
            ) : null}

            {tab === "summary" ? (
              noPlans && !selectedCurrency ? (
                <div className="ss-empty">
                  <i className="fas fa-layer-group" />
                  <p>This user has no staking plans</p>
                </div>
              ) : (
                <section>
                  <div className="ss-cards">
                    <AmountCard
                      title="Staked"
                      field="staked"
                      value={selectedCurrency?.staked}
                      currency={currencyCode}
                      hint="Principal locked"
                      tone="grey"
                    />
                    <AmountCard
                      title="Expected return"
                      field="expected_return"
                      value={selectedCurrency?.expected_return}
                      currency={currencyCode}
                      hint="Principal + profit"
                    />
                    <AmountCard
                      title="Interest (upper profit)"
                      field="interest_total"
                      value={selectedCurrency?.interest_total}
                      currency={currencyCode}
                      hint="Expected − staked"
                    />
                    <AmountCard
                      title="Payout paid"
                      field="payout_paid"
                      value={selectedCurrency?.payout_paid}
                      currency={currencyCode}
                      hint="Wallet me ja chuka"
                      tone="green"
                    />
                    <AmountCard
                      title="Payout remaining"
                      field="payout_remaining"
                      value={selectedCurrency?.payout_remaining}
                      currency={currencyCode}
                      hint="Abhi jana baaki"
                      tone="orange"
                    />
                    <AmountCard
                      title="Interest paid"
                      field="interest_paid"
                      value={selectedCurrency?.interest_paid}
                      currency={currencyCode}
                      hint="Profit me se nikla"
                      tone="green"
                    />
                    <AmountCard
                      title="Interest remaining"
                      field="interest_remaining"
                      value={selectedCurrency?.interest_remaining}
                      currency={currencyCode}
                      tone="orange"
                    />
                    <AmountCard
                      title="Principal paid"
                      field="principal_paid"
                      value={selectedCurrency?.principal_paid}
                      currency={currencyCode}
                    />
                    <AmountCard
                      title="Principal remaining"
                      field="principal_remaining"
                      value={selectedCurrency?.principal_remaining}
                      currency={currencyCode}
                      hint="Maturity / last period pe lautega"
                    />
                    <AmountCard
                      title="Forfeited"
                      field="payout_forfeited"
                      value={selectedCurrency?.payout_forfeited}
                      currency={currencyCode}
                      tone="red"
                      hideIfZero
                    />
                    <AmountCard
                      title="Refunded"
                      field="refunded"
                      value={selectedCurrency?.refunded}
                      currency={currencyCode}
                      hideIfZero
                    />
                    <AmountCard
                      title="Cancel fee"
                      field="cancel_fee"
                      value={selectedCurrency?.cancel_fee}
                      currency={currencyCode}
                      hideIfZero
                    />
                  </div>
                  <div className="ss-progress-wrap">
                    <ProgressBar
                      label="Interest paid / total"
                      paid={selectedCurrency?.interest_paid}
                      total={selectedCurrency?.interest_total}
                    />
                    <ProgressBar
                      label="Payout paid / expected return"
                      paid={selectedCurrency?.payout_paid}
                      total={selectedCurrency?.expected_return}
                    />
                  </div>
                  <p className="ss-note">
                    Without-principal packages pay interest each period. Principal returns on the last
                    period as interest + principal, so payout remaining can still include locked principal.
                  </p>
                </section>
              )
            ) : null}

            {tab === "plans" ? (
              subscriptions.length === 0 ? (
                <div className="ss-empty">
                  <i className="fas fa-layer-group" />
                  <p>This user has no staking plans</p>
                </div>
              ) : (
                <div className="ss-plan-grid">
                  {subscriptions.map((plan) => {
                    const pkg = plan.package || {};
                    const ccy = plan.currency || pkg.currency || currencyCode;
                    const cancelled = String(plan.status || "").toUpperCase() === "CANCELLED";
                    const active = String(plan.status || "").toUpperCase() === "ACTIVE";
                    const id = plan.subscription_id || plan._id || plan.id;
                    return (
                      <article key={id} className="ss-plan-card">
                        <header className="ss-plan-card__head">
                          <div>
                            <strong>
                              {[pkg.currency || ccy, pkg.duration_days != null ? `${pkg.duration_days}d` : null, pkg.return_percentage != null ? `${pkg.return_percentage}%` : null]
                                .filter(Boolean)
                                .join(" · ") || "Plan"}
                            </strong>
                            <div className="ss-plan-card__badges">
                              <PlanStatusBadge status={plan.status} />
                              <span
                                className={`ss-badge ss-badge--${
                                  String(plan.payout_status).toLowerCase() === "inactive"
                                    ? "red"
                                    : "green"
                                }`}
                              >
                                Payout {plan.payout_status || "active"}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="staking-stakes__view-btn"
                            onClick={() => viewPlanInStatement(id)}
                          >
                            View in statement
                          </button>
                        </header>
                        <dl className="ss-plan-dl">
                          <div>
                            <dt>Type</dt>
                            <dd>{plan.subscription_type || "—"}</dd>
                          </div>
                          <div>
                            <dt>With principal</dt>
                            <dd>{plan.with_principal_amount ? "Yes" : "No"}</dd>
                          </div>
                          <div>
                            <dt>
                              Staked <Help field="staked" />
                            </dt>
                            <dd>{formatMoney(plan.staked, ccy)}</dd>
                          </div>
                          <div>
                            <dt>
                              Expected <Help field="expected_return" />
                            </dt>
                            <dd>{formatMoney(plan.expected_return, ccy)}</dd>
                          </div>
                          <div>
                            <dt>
                              Paid so far <Help field="payout_paid" />
                            </dt>
                            <dd className="ss-amt--paid">{formatMoney(plan.payout_paid, ccy)}</dd>
                          </div>
                          <div>
                            <dt>
                              Still due <Help field="payout_remaining" />
                            </dt>
                            <dd className="ss-amt--remain">{formatMoney(plan.payout_remaining, ccy)}</dd>
                          </div>
                          <div>
                            <dt>Interest paid</dt>
                            <dd>
                              {formatMoney(plan.interest_paid, ccy)} / {formatMoney(plan.interest_total, ccy)}
                            </dd>
                          </div>
                          <div>
                            <dt>Periods</dt>
                            <dd>
                              {plan.paid_days ?? 0} / {plan.total_periods ?? "—"}
                            </dd>
                          </div>
                          <div>
                            <dt>Start / end</dt>
                            <dd>
                              {formatDateShort(plan.start_date)} → {formatDateShort(plan.end_date)}
                            </dd>
                          </div>
                          {active && plan.next_payout_date ? (
                            <div>
                              <dt>Next payout</dt>
                              <dd>{formatDateShort(plan.next_payout_date)}</dd>
                            </div>
                          ) : null}
                          {plan.last_payout_at ? (
                            <div>
                              <dt>Last payout</dt>
                              <dd>{formatDateTime(plan.last_payout_at)}</dd>
                            </div>
                          ) : null}
                          {cancelled ? (
                            <>
                              <div>
                                <dt>
                                  Fee <Help field="cancel_fee" />
                                </dt>
                                <dd>{formatMoney(plan.cancel_fee, ccy)}</dd>
                              </div>
                              <div>
                                <dt>
                                  Refund <Help field="refunded" />
                                </dt>
                                <dd>{formatMoney(plan.refunded, ccy)}</dd>
                              </div>
                              <div>
                                <dt>
                                  Forfeited ROI <Help field="payout_forfeited" />
                                </dt>
                                <dd className="ss-amt--forfeit">{formatMoney(plan.payout_forfeited, ccy)}</dd>
                              </div>
                            </>
                          ) : null}
                        </dl>
                      </article>
                    );
                  })}
                </div>
              )
            ) : null}

            {tab === "statement" ? (
              <section>
                <div className="ss-ledger-controls">
                  <label className="ss-toggle">
                    <input
                      type="checkbox"
                      checked={includePending}
                      onChange={(e) => {
                        setIncludePending(e.target.checked);
                        setStatementPage(1);
                      }}
                    />
                    Show upcoming
                  </label>
                  <select
                    value={subscriptionId}
                    onChange={(e) => {
                      setSubscriptionId(e.target.value);
                      setStatementPage(1);
                    }}
                    className="openorders-search-input"
                  >
                    <option value="">All plans</option>
                    {subscriptionOptions.map((plan) => {
                      const id = plan.subscription_id || plan._id || plan.id;
                      const pkg = plan.package || {};
                      const ccy = plan.currency || pkg.currency || "";
                      return (
                        <option key={id} value={id}>
                          {(ccy || "Plan") +
                            (pkg.duration_days != null ? ` · ${pkg.duration_days}d` : "") +
                            ` · …${subscriptionShortId(id)}`}
                        </option>
                      );
                    })}
                  </select>
                  <span className="openorders-stat" style={{ margin: 0 }}>
                    Page size {STATEMENT_PAGE_LIMIT}
                  </span>
                </div>

                {noPlans ? (
                  <div className="ss-empty">
                    <i className="fas fa-layer-group" />
                    <p>This user has no staking plans</p>
                  </div>
                ) : statement.length === 0 ? (
                  <div className="ss-empty">
                    <i className="fas fa-book" />
                    <p>{includePending ? "No statement lines" : "No paid payouts yet"}</p>
                  </div>
                ) : (
                  <>
                    <div className="openorders-table-wrap">
                      <table className="openorders-table ss-ledger">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Type</th>
                            <th>Description</th>
                            <th>Plan</th>
                            <th>Debit</th>
                            <th>Credit</th>
                            <th>Interest</th>
                            <th>Principal</th>
                            <th>Status</th>
                            <th>Running credited</th>
                            <th>Running remaining</th>
                          </tr>
                        </thead>
                        <tbody>
                          {statement.map((line, index) => {
                            const pending = String(line.status || "").toLowerCase() === "pending";
                            const ccy = line.currency || currencyCode;
                            const typeKey = String(line.type || "").toUpperCase();
                            const payoutHint = typeKey === "PAYOUT" ? payoutTypeLabel(line.payout_type) : "";
                            return (
                              <tr
                                key={`${line.subscription_id || "line"}-${line.date || index}-${index}`}
                                className={pending ? "ss-ledger__row--pending" : ""}
                              >
                                <td>{formatDateTime(line.date)}</td>
                                <td>
                                  <TypeBadge type={line.type} />
                                  {payoutHint ? (
                                    <small className="ss-ledger__hint">{payoutHint}</small>
                                  ) : null}
                                </td>
                                <td>{line.description || "—"}</td>
                                <td>
                                  <div>{line.period_label || "—"}</div>
                                  {line.subscription_id ? (
                                    <code className="ss-mini-id">
                                      …{subscriptionShortId(line.subscription_id)}
                                    </code>
                                  ) : null}
                                </td>
                                <td className="ss-amt--forfeit">
                                  {hasPositive(line.debit) ? formatMoney(line.debit, ccy) : "—"}
                                </td>
                                <td className="ss-amt--paid">
                                  {hasPositive(line.credit) ? formatMoney(line.credit, ccy) : "—"}
                                </td>
                                <td>{hasPositive(line.interest) || line.interest === 0 ? formatMoney(line.interest, ccy) : "—"}</td>
                                <td>{hasPositive(line.principal) || line.principal === 0 ? formatMoney(line.principal, ccy) : "—"}</td>
                                <td>
                                  <LineStatusBadge status={line.status} />
                                </td>
                                <td>{line.running_credited != null ? formatMoney(line.running_credited, ccy) : "—"}</td>
                                <td>{line.running_remaining != null ? formatMoney(line.running_remaining, ccy) : "—"}</td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="ss-pagination">
                      <button
                        type="button"
                        className="cp-btn cp-btn--outline"
                        disabled={!statementPagination.has_prev || loading || statementPage <= 1}
                        onClick={() => setStatementPage((p) => Math.max(1, p - 1))}
                      >
                        Previous
                      </button>
                      <span className="openorders-stat" style={{ margin: 0 }}>
                        Page {statementPagination.page || statementPage} of{" "}
                        {statementPagination.total_pages || 1}
                        {statementPagination.total != null
                          ? ` · ${statementPagination.total} lines`
                          : ""}
                      </span>
                      <button
                        type="button"
                        className="cp-btn cp-btn--outline"
                        disabled={
                          !statementPagination.has_next ||
                          loading ||
                          statementPage >= (statementPagination.total_pages || 1)
                        }
                        onClick={() => setStatementPage((p) => p + 1)}
                      >
                        Next
                      </button>
                    </div>
                  </>
                )}
              </section>
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  );
};

export default StakingStatementDetailPage;
